import { useCallback, useEffect, useState } from "react";
import {
  exchangeProduct,
  fetchCoupon,
  fetchCouponList,
  fetchGroupList,
  fetchPointHistory,
  fetchStore,
  newRequestKey,
  searchClassmates,
  startGroupPurchase,
} from "./rewardAPI";
import { announceBalance, formatPoint } from "./pointStore";
import { showGrade } from "../../utils/textUtils";
import { CoinIcon, FriendsIcon, HistoryIcon, TicketIcon } from "./RewardIcons";
import "./Store.css";

// 학생 포인트 상점(2026-10-04). 상단 바의 포인트 · 상점 단추를 누르면 온다(/store?session=...).
// 학원이 고른 기프티콘만 보인다. 필요 포인트는 정가 그대로다(1원 = 1P).

const TAB_LIST = [
  { name: "store", text: "상점", Icon: CoinIcon },
  { name: "group", text: "같이 사기", Icon: FriendsIcon },
  { name: "coupons", text: "내 쿠폰함", Icon: TicketIcon },
  { name: "history", text: "포인트 내역", Icon: HistoryIcon },
];

// 상품 사진이 아직 없으니(상용 연결 전) 브랜드 색 카드로 보여 준다
const BRAND_COLOR = {
  배스킨라빈스: "#F5A3C7",
  요아정: "#FFD9E2",
  CU: "#6FCF3F",
  GS25: "#4DB8FF",
  세븐일레븐: "#FF8A3D",
  이마트24: "#FFC23D",
  올리브영: "#9ACD32",
  파리바게뜨: "#3D6CB5",
  스타벅스: "#1E8C5A",
  메가커피: "#FFD43B",
  배달의민족: "#3DD6C6",
  네이버페이: "#03C75A",
  구글플레이: "#8AB4F8",
};
const brandColor = (brandName) => BRAND_COLOR[brandName] || "#D6DEE0";
// 옅은 바탕(요아정·메가커피 등)에 흰 글씨는 안 보인다. 밝기를 보고 글씨색을 고른다
const brandTextColor = (brandName) => {
  const hex = brandColor(brandName).replace("#", "");
  const [red, green, blue] = [0, 2, 4].map((at) => parseInt(hex.slice(at, at + 2), 16));
  return red * 0.299 + green * 0.587 + blue * 0.114 > 186 ? "#0E141B" : "#FFFFFF";
};
const MAX_FRIEND_COUNT = 5;

const formatDate = (value) => (value ? new Date(value).toLocaleDateString("ko-KR") : "");

// 기프티쇼를 잇기 전 임시 브랜드 칸(2026-10-05 대표가 준 그림, public/reward-brands). 공식 로고가 아니다.
// 기프티쇼 상품 사진(image_url)이나 브랜드 아이콘(brand_icon_url)이 들어오면 그것이 먼저다.
const TEMP_BRAND_TILE = {
  배스킨라빈스: "baskinrobbins",
  요아정: "yoajung",
  CU: "cu",
  GS25: "gs25",
  세븐일레븐: "7eleven",
  이마트24: "emart24",
  올리브영: "oliveyoung",
  파리바게뜨: "parisbaguette",
  스타벅스: "starbucks",
  메가커피: "megacoffee",
  메가MGC커피: "megacoffee",
  배달의민족: "baemin",
  네이버페이: "naverpay",
  구글플레이: "googleplay",
};
const tempBrandTileUrl = (brandName) =>
  TEMP_BRAND_TILE[brandName] ? `${process.env.PUBLIC_URL}/reward-brands/${TEMP_BRAND_TILE[brandName]}.png` : "";

const ProductImage = ({ product, size = "md" }) =>
  product.image_url || (!product.brand_icon_url && tempBrandTileUrl(product.brand_name)) ? (
    <img
      className={`store-thumb store-thumb--${size}`}
      src={product.image_url || tempBrandTileUrl(product.brand_name)}
      alt=""
    />
  ) : (
    <div
      className={`store-thumb store-thumb--${size}`}
      style={{ backgroundColor: brandColor(product.brand_name), color: brandTextColor(product.brand_name) }}
    >
      {product.brand_icon_url ? (
        <img className="store-thumb__icon" src={product.brand_icon_url} alt={product.brand_name} />
      ) : (
        <span>{product.brand_name}</span>
      )}
    </div>
  );

// 상용 키 전 예시 쿠폰은 바코드 이미지가 없어서 쿠폰 번호로 막대를 그린다
const SampleBarcode = ({ pinNo }) => (
  <div className="store-barcode" aria-hidden="true">
    {String(pinNo || "")
      .split("")
      .flatMap((digit, index) => [
        <i key={`b${index}`} style={{ width: `${1 + (Number(digit) % 3)}px` }} />,
        <b key={`s${index}`} style={{ width: `${1 + ((Number(digit) + index) % 2)}px` }} />,
      ])}
  </div>
);

const CouponDetail = ({ coupon, onClose }) => (
  <div className="store-sheet" onClick={onClose}>
    <div className="store-sheet__body" onClick={(event) => event.stopPropagation()}>
      <div className="store-coupon">
        <ProductImage product={coupon.product} size="lg" />
        <div className="store-coupon__brand">{coupon.product.brand_name}</div>
        <div className="store-coupon__name">{coupon.product.goods_name}</div>
        {coupon.status === "issued" ? (
          <>
            {coupon.coupon_image_url ? (
              <img className="store-coupon__image" src={coupon.coupon_image_url} alt="쿠폰 바코드" />
            ) : (
              <SampleBarcode pinNo={coupon.pin_no} />
            )}
            <div className="store-coupon__pin">{coupon.pin_no}</div>
            {coupon.valid_until && <div className="store-coupon__valid">{formatDate(coupon.valid_until)}까지</div>}
            {coupon.is_sample && <div className="store-coupon__sample">예시 쿠폰 · 매장에서 쓸 수 없어요</div>}
          </>
        ) : (
          <div className="store-coupon__message">{coupon.message || coupon.status_label}</div>
        )}
      </div>
      <button type="button" className="store-button store-button--ghost" onClick={onClose}>
        닫기
      </button>
    </div>
  </div>
);

const FriendPicker = ({ product, balance, onDone, onClose }) => {
  const [keyword, setKeyword] = useState("");
  const [resultList, setResultList] = useState([]);
  const [friendList, setFriendList] = useState([]);
  const [message, setMessage] = useState("");
  const [isBusy, setIsBusy] = useState(false);

  const people = friendList.length + 1;
  const share = Math.floor(product.point_price / people);
  const myShare = product.point_price - share * friendList.length;

  const search = async (event) => {
    event.preventDefault();
    setMessage("");
    try {
      const data = await searchClassmates(keyword);
      setResultList(data.student_list);
      if (data.student_list.length === 0) setMessage("그 이름의 친구를 찾지 못했어요.");
    } catch (error) {
      setMessage(error.message);
    }
  };

  const pick = (friend) => {
    if (friendList.some((one) => one.id === friend.id) || friendList.length >= MAX_FRIEND_COUNT) return;
    setFriendList([...friendList, friend]);
    setResultList([]);
    setKeyword("");
  };

  const start = async () => {
    setIsBusy(true);
    setMessage("");
    try {
      const data = await startGroupPurchase(product.id, friendList.map((one) => one.id));
      announceBalance(data.balance);
      onDone();
    } catch (error) {
      setMessage(error.message);
    }
    setIsBusy(false);
  };

  return (
    <div className="store-sheet" onClick={onClose}>
      <div className="store-sheet__body" onClick={(event) => event.stopPropagation()}>
        <div className="store-sheet__head">
          <ProductImage product={product} size="sm" />
          <div>
            <div className="store-sheet__title">친구랑 같이 사기</div>
            <div className="store-sheet__sub">
              {product.goods_name} · {formatPoint(product.point_price)}
            </div>
          </div>
        </div>
        <form className="store-search" onSubmit={search}>
          <input value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="친구 이름" />
          <button type="submit" className="store-button store-button--ghost">
            찾기
          </button>
        </form>
        {resultList.map((friend) => (
          <button type="button" key={friend.id} className="store-friend" onClick={() => pick(friend)}>
            <b>{friend.name}</b>
            <span>{showGrade(friend.grade)}</span>
          </button>
        ))}
        <div className="store-split">
          <div className="store-split__row">
            <span>나</span>
            <b>{formatPoint(myShare)}</b>
          </div>
          {friendList.map((friend) => (
            <div className="store-split__row" key={friend.id}>
              <span>{friend.name}</span>
              <b>{formatPoint(share)}</b>
              <button type="button" onClick={() => setFriendList(friendList.filter((one) => one.id !== friend.id))}>
                빼기
              </button>
            </div>
          ))}
        </div>
        {message && <div className="store-message">{message}</div>}
        <button
          type="button"
          className="store-button"
          onClick={start}
          disabled={isBusy || friendList.length === 0 || (balance ?? 0) < myShare}
        >
          {(balance ?? 0) < myShare ? "내 몫 포인트가 모자라요" : "친구에게 물어보기"}
        </button>
        <button type="button" className="store-button store-button--ghost" onClick={onClose}>
          닫기
        </button>
      </div>
    </div>
  );
};

// canExchange 가 false 면(기프티쇼를 진짜로 잇기 전) 상품만 보여 주고 교환 단추를 막는다
const ProductSheet = ({ product, balance, canExchange, onExchanged, onGroup, onClose }) => {
  const [isBusy, setIsBusy] = useState(false);
  const [message, setMessage] = useState("");
  const isEnough = (balance ?? 0) >= product.point_price;

  const exchange = async () => {
    setIsBusy(true);
    setMessage("");
    try {
      const data = await exchangeProduct(product.id, newRequestKey());
      announceBalance(data.balance);
      onExchanged(data.order);
    } catch (error) {
      setMessage(error.message);
      setIsBusy(false);
    }
  };

  return (
    <div className="store-sheet" onClick={onClose}>
      <div className="store-sheet__body" onClick={(event) => event.stopPropagation()}>
        <div className="store-sheet__head">
          <ProductImage product={product} size="sm" />
          <div>
            <div className="store-sheet__title">{product.goods_name}</div>
            <div className="store-sheet__sub">{product.brand_name}</div>
          </div>
        </div>
        <div className="store-price">
          <CoinIcon size="0.9em" /> {formatPoint(product.point_price)}
        </div>
        {message && <div className="store-message">{message}</div>}
        {canExchange ? (
          <>
            <button type="button" className="store-button" onClick={exchange} disabled={isBusy || !isEnough}>
              {isEnough ? "교환하기" : `${formatPoint(product.point_price - (balance ?? 0))} 더 모으면 교환할 수 있어요`}
            </button>
            <button type="button" className="store-button store-button--friend" onClick={onGroup}>
              <FriendsIcon /> 친구랑 같이 사기
            </button>
          </>
        ) : (
          <button type="button" className="store-button" disabled>
            교환은 곧 열려요
          </button>
        )}
        <button type="button" className="store-button store-button--ghost" onClick={onClose}>
          닫기
        </button>
      </div>
    </div>
  );
};

const StoreTab = ({ onShowCoupon }) => {
  const [data, setData] = useState(null);
  const [message, setMessage] = useState("");
  const [picked, setPicked] = useState(null);
  const [groupProduct, setGroupProduct] = useState(null);

  const load = useCallback(() => {
    fetchStore()
      .then((result) => {
        setData(result);
        announceBalance(result.balance);
      })
      .catch((error) => setMessage(error.message));
  }, []);
  useEffect(load, [load]);

  if (message) return <div className="store-empty">{message}</div>;
  if (!data) return null;
  if (data.product_list.length === 0) return <div className="store-empty">아직 상점에 상품이 없어요.</div>;

  return (
    <>
      <div className="store-grid">
        {data.product_list.map((product) => (
          <button type="button" key={product.id} className="store-card" onClick={() => setPicked(product)}>
            <ProductImage product={product} />
            <div className="store-card__brand">
              {product.brand_icon_url && <img className="store-card__brand-icon" src={product.brand_icon_url} alt="" />}
              {product.brand_name}
            </div>
            <div className="store-card__name">{product.goods_name}</div>
            <div className={`store-card__price${data.balance >= product.point_price ? " store-card__price--ok" : ""}`}>
              {formatPoint(product.point_price)}
            </div>
          </button>
        ))}
      </div>
      {picked && (
        <ProductSheet
          canExchange={data.can_exchange !== false}
          product={picked}
          balance={data.balance}
          onClose={() => setPicked(null)}
          onGroup={() => {
            setGroupProduct(picked);
            setPicked(null);
          }}
          onExchanged={(order) => {
            setPicked(null);
            load();
            onShowCoupon(order);
          }}
        />
      )}
      {groupProduct && (
        <FriendPicker
          product={groupProduct}
          balance={data.balance}
          onClose={() => setGroupProduct(null)}
          onDone={() => {
            setGroupProduct(null);
            load();
            alert("친구에게 물어봤어요. 모두 좋다고 하면 쿠폰이 생겨요.");
          }}
        />
      )}
    </>
  );
};

const GROUP_STATUS_LABEL = { waiting: "친구 대답 기다리는 중", completed: "쿠폰 받음", canceled: "취소됨" };
const MEMBER_STATUS_LABEL = { invited: "기다리는 중", agreed: "좋아요", declined: "안 할래요" };

const GroupTab = () => {
  const [groupList, setGroupList] = useState(null);
  useEffect(() => {
    fetchGroupList()
      .then((data) => setGroupList(data.group_list))
      .catch(() => setGroupList([]));
  }, []);
  if (!groupList) return null;
  if (groupList.length === 0) return <div className="store-empty">같이 산 적이 아직 없어요.</div>;
  return (
    <div className="store-list">
      {groupList.map((group) => (
        <div className="store-row store-row--group" key={group.id}>
          <ProductImage product={group.product} size="sm" />
          <div className="store-row__main">
            <b>{group.product.goods_name}</b>
            <span>
              {GROUP_STATUS_LABEL[group.status]}
              {group.cancel_reason && ` · ${group.cancel_reason}`}
            </span>
            <div className="store-members">
              {group.member_list.map((member) => (
                <em key={member.name} className={`store-member store-member--${member.status}`}>
                  {member.name} {formatPoint(member.share_points)} · {MEMBER_STATUS_LABEL[member.status]}
                </em>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const CouponTab = ({ onShowCoupon }) => {
  const [couponList, setCouponList] = useState(null);
  useEffect(() => {
    fetchCouponList()
      .then((data) => setCouponList(data.coupon_list))
      .catch(() => setCouponList([]));
  }, []);
  if (!couponList) return null;
  if (couponList.length === 0) return <div className="store-empty">아직 받은 쿠폰이 없어요.</div>;
  return (
    <div className="store-list">
      {couponList.map((coupon) => (
        <button type="button" className="store-row" key={coupon.id} onClick={() => onShowCoupon(coupon)}>
          <ProductImage product={coupon.product} size="sm" />
          <div className="store-row__main">
            <b>{coupon.product.goods_name}</b>
            <span>
              {formatDate(coupon.created_at)} · {coupon.status_label}
              {coupon.valid_until && ` · ${formatDate(coupon.valid_until)}까지`}
            </span>
          </div>
          <span className="store-row__go">보기</span>
        </button>
      ))}
    </div>
  );
};

const HistoryTab = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetchPointHistory()
      .then(setData)
      .catch(() => setData({ history: [] }));
  }, []);
  if (!data) return null;
  if (data.history.length === 0) return <div className="store-empty">아직 포인트 내역이 없어요.</div>;
  return (
    <div className="store-list">
      {data.history.map((row) => (
        <div className="store-row" key={row.id}>
          <div className="store-row__main">
            <b>{row.description}</b>
            <span>{formatDate(row.created_at)}</span>
          </div>
          <div className={`store-amount ${row.amount >= 0 ? "store-amount--plus" : "store-amount--minus"}`}>
            {row.amount >= 0 ? "+" : ""}
            {formatPoint(row.amount)}
          </div>
        </div>
      ))}
    </div>
  );
};

const StorePage = () => {
  const [tab, setTab] = useState("store");
  const [coupon, setCoupon] = useState(null);

  // 목록의 쿠폰에는 번호가 없다. 열 때 그 쿠폰만 다시 받는다(번호는 본인에게만 준다)
  const showCoupon = async (item) => {
    if (item.pin_no !== undefined) {
      setCoupon(item);
      return;
    }
    try {
      const data = await fetchCoupon(item.id);
      setCoupon(data.coupon);
    } catch (error) {
      alert(error.message);
    }
  };

  return (
    <div className="store-page">
      <div className="store-tabs">
        {TAB_LIST.map((one) => (
          <button
            type="button"
            key={one.name}
            className={`store-tab${tab === one.name ? " store-tab--active" : ""}`}
            onClick={() => setTab(one.name)}
          >
            <one.Icon size="1.1em" />
            {one.text}
          </button>
        ))}
      </div>
      {tab === "store" && <StoreTab onShowCoupon={showCoupon} />}
      {tab === "group" && <GroupTab />}
      {tab === "coupons" && <CouponTab onShowCoupon={showCoupon} />}
      {tab === "history" && <HistoryTab />}
      {coupon && <CouponDetail coupon={coupon} onClose={() => setCoupon(null)} />}
    </div>
  );
};

export default StorePage;
